import React, { useMemo, useState } from 'react';
import { Button, Form, InputGroup, Overlay, Popover } from 'react-bootstrap';
import {
  Calendar3 as FiCalendar,
  ChevronLeft,
  ChevronRight,
  X,
} from 'react-bootstrap-icons';
import styles from './dateField.module.scss';

export interface DateFieldProps {
  label?: string;
  labelClassName?: string;
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  groupClassName?: string;
}

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function parseIsoDate(value: string): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

function formatDisplayDate(iso: string): string {
  const dt = parseIsoDate(iso);
  if (!dt) return '';
  return dt.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function buildCalendarDays(viewMonth: Date): Array<Date | null> {
  const first = startOfMonth(viewMonth);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() + 1,
    0
  ).getDate();

  const cells: Array<Date | null> = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const DateField: React.FC<DateFieldProps> = ({
  label,
  labelClassName,
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  id,
  disabled = false,
  groupClassName,
}) => {
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState<HTMLElement | null>(null);

  const selected = parseIsoDate(value);
  const [viewMonth, setViewMonth] = useState<Date>(
    () => selected ?? new Date()
  );

  const monthLabel = useMemo(
    () =>
      viewMonth.toLocaleDateString('es-ES', {
        month: 'long',
        year: 'numeric',
      }),
    [viewMonth]
  );

  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const todayIso = toIsoDate(new Date());

  const openCalendar = () => {
    if (disabled) return;
    if (selected) setViewMonth(startOfMonth(selected));
    setShow(true);
  };

  const handleSelect = (day: Date) => {
    onChange(toIsoDate(day));
    setShow(false);
  };

  const handleClear = () => {
    onChange('');
    setShow(false);
  };

  return (
    <Form.Group controlId={id} className={groupClassName}>
      {label ? (
        <Form.Label className={labelClassName}>{label}</Form.Label>
      ) : null}
      <InputGroup ref={setTarget} className={styles.inputGroup}>
        <Form.Control
          readOnly
          disabled={disabled}
          value={value ? formatDisplayDate(value) : ''}
          placeholder={placeholder}
          className={styles.displayInput}
          onClick={openCalendar}
        />
        {value && !disabled ? (
          <Button
            variant="outline-secondary"
            onClick={handleClear}
            title="Limpiar fecha"
            aria-label="Limpiar fecha"
          >
            <X size={14} />
          </Button>
        ) : null}
        <Button
          variant="outline-secondary"
          onClick={openCalendar}
          disabled={disabled}
          title="Abrir calendario"
          aria-label="Abrir calendario"
        >
          <FiCalendar size={16} />
        </Button>
      </InputGroup>

      <Overlay
        show={show && !disabled}
        target={target}
        placement="bottom-start"
        rootClose
        onHide={() => setShow(false)}
      >
        <Popover className={styles.popover}>
          <Popover.Body className={styles.popoverBody}>
            <div className={styles.header}>
              <Button
                variant="link"
                className={styles.navBtn}
                onClick={() => setViewMonth(prev => addMonths(prev, -1))}
                aria-label="Mes anterior"
              >
                <ChevronLeft size={18} />
              </Button>
              <span className={styles.monthLabel}>{monthLabel}</span>
              <Button
                variant="link"
                className={styles.navBtn}
                onClick={() => setViewMonth(prev => addMonths(prev, 1))}
                aria-label="Mes siguiente"
              >
                <ChevronRight size={18} />
              </Button>
            </div>

            <div className={styles.weekdays}>
              {WEEKDAYS.map(w => (
                <span key={w} className={styles.weekday}>
                  {w}
                </span>
              ))}
            </div>

            <div className={styles.grid}>
              {calendarDays.map((day, idx) => {
                if (!day) {
                  return (
                    <span key={`empty-${idx}`} className={styles.emptyCell} />
                  );
                }
                const iso = toIsoDate(day);
                const isSelected = value === iso;
                const isToday = todayIso === iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    className={[
                      styles.dayBtn,
                      isSelected ? styles.daySelected : '',
                      isToday ? styles.dayToday : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleSelect(day)}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <div className={styles.footer}>
              <Button
                variant="link"
                size="sm"
                className={styles.todayBtn}
                onClick={() => handleSelect(new Date())}
              >
                Hoy
              </Button>
              {value ? (
                <Button
                  variant="link"
                  size="sm"
                  className={styles.clearBtn}
                  onClick={handleClear}
                >
                  Limpiar
                </Button>
              ) : null}
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </Form.Group>
  );
};

export default DateField;
